import { useState, useContext, useEffect } from "react";
import { trpc } from "../../trpc/index";
import MerchantContext from "../../contexts/MerchantContext";
import { Skeleton } from "@/components/ui/skeleton";
import SectionTitle from "../../components/SectionTitle";
import BillablePeriodCard from "../../components/consumption/BillablePeriodCard";
import ConversationStatsCard from "../../components/consumption/BillableConsumptionCard";
import ConversationsBilledCard from "../../components/consumption/BillableConversationsCard";
import BillableReportCard from "../../components/consumption/BillableReportCard";
import MainSectionSection from "../../components/MainSectionSection";

const ConsumptionContainer = () => {
  const [dateRange, setDateRange] = useState<{ startDate: string; endDate: string }>({
    startDate: "",
    endDate: "",
  });
  const { merchantId } = useContext(MerchantContext);

  const { data: billableInformation, isLoading } =
    trpc.billableEvent.getBillableInformation.useQuery(
      {
        merchantId,
      },
      {
        enabled: !!merchantId,
        keepPreviousData: false,
      }
    );

  // Set default date range when billableInformation loads
  useEffect(() => {
    if (billableInformation?.billingCycleDay && !dateRange.startDate && !dateRange.endDate) {
      const today = new Date();
      const currentYear = today.getFullYear();
      const currentMonth = today.getMonth();
      const currentDay = today.getDate();

      // Determine the current billing period
      const isAfterBillingDay = currentDay >= billableInformation.billingCycleDay;
      const billingMonth = isAfterBillingDay ? currentMonth : currentMonth - 1;
      const billingYear = billingMonth < 0 ? currentYear - 1 : currentYear;
      const adjustedBillingMonth = billingMonth < 0 ? 11 : billingMonth;

      const startDate = new Date(
        billingYear,
        adjustedBillingMonth,
        billableInformation.billingCycleDay
      );
      const endDate = new Date(
        billingYear,
        adjustedBillingMonth + 1,
        billableInformation.billingCycleDay - 1
      );

      const formatDate = (date: Date): string => {
        const year = date.getFullYear();
        const month = (date.getMonth() + 1).toString().padStart(2, "0");
        const day = date.getDate().toString().padStart(2, "0");
        return `${year}-${month}-${day}`;
      };

      const defaultDateRange = {
        startDate: formatDate(startDate),
        endDate: formatDate(endDate),
      };

      setDateRange(defaultDateRange);
    }
  }, [billableInformation, dateRange.startDate, dateRange.endDate]);

  const { data: billableConsumption, isLoading: isLoadingConsumption } =
    trpc.billableEvent.getBillableConsumption.useQuery(
      {
        merchantId,
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
      },
      {
        enabled: !!merchantId && !!dateRange.startDate && !!dateRange.endDate,
      }
    );

  const handleSetDateRange = (newDateRange: { startDate: string; endDate: string }) => {
    setDateRange(newDateRange);
  };

  return (
    <MainSectionSection gap="24px">
      <SectionTitle title="Consumo" />

      <p className="mb-6 mt-2 text-gray-secondary">
        Monitoree el uso de su suscripción, período de facturación y cargos por conversación
      </p>

      <div className="mb-8 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
        <div>
          <BillablePeriodCard
            calendarSubscriptionStartDay={billableInformation?.billingCycleDay}
            setDateRange={handleSetDateRange}
          />
        </div>

        {isLoading ? (
          <>
            <div>
              <Skeleton className="h-[200px] w-full rounded-lg" />
            </div>
            <div>
              <Skeleton className="h-[200px] w-full rounded-lg" />
            </div>
          </>
        ) : (
          <>
            <div>
              {isLoadingConsumption ? (
                <Skeleton className="h-[200px] w-full rounded-lg" />
              ) : (
                <ConversationStatsCard
                  charged={billableConsumption?.consumption ?? 0}
                  total={
                    billableConsumption?.minimumSubscriptionUsage ??
                    billableInformation?.minimumSubscriptionUsage ??
                    0
                  }
                  value={billableConsumption?.consumption ?? 0}
                  conversationCost={billableConsumption?.conversationCost ?? 0}
                  callMinutesCost={billableConsumption?.callMinutesCost ?? 0}
                  outboundCost={billableConsumption?.outboundCost ?? 0}
                  isInvoiced={billableConsumption?.isInvoiced ?? false}
                />
              )}
            </div>
            <div>
              {isLoadingConsumption ? (
                <Skeleton className="h-[200px] w-full rounded-lg" />
              ) : (
                <ConversationsBilledCard
                  conversationsTotal={billableConsumption?.conversations}
                  conversationsCost={billableConsumption?.conversationCost}
                  outboundsTotal={billableConsumption?.outbounds}
                  outboundsCost={billableConsumption?.outboundCost}
                  callMinutesTotal={billableConsumption?.callMinutes}
                  callMinutesCost={billableConsumption?.callMinutesCost}
                />
              )}
            </div>
          </>
        )}
      </div>

      {/* Billable Report Download Section */}
      <BillableReportCard merchantId={merchantId} />
    </MainSectionSection>
  );
};

export default ConsumptionContainer;
